export const CANVAS_DATABASE_PRESENTATION_VIEW_MODES = [
  'summary',
  'table',
  'kanban',
  'gantt',
] as const;

export type CanvasDatabasePresentationViewMode =
  (typeof CANVAS_DATABASE_PRESENTATION_VIEW_MODES)[number];

export type CanvasDatabaseInteractionZone =
  | 'select-move'
  | 'horizontal-resize'
  | 'content-edit'
  | 'toolbar-action'
  | 'fullscreen-action'
  | 'internal-window'
  | 'import-drop';

export type CanvasDatabaseFirstPointerExpectation =
  | 'select-node'
  | 'resize-node'
  | 'activate-target'
  | 'scroll-preview'
  | 'run-import-preflight';

export interface CanvasDatabaseInteractionZoneContract {
  readonly zone: CanvasDatabaseInteractionZone;
  readonly firstPointer: CanvasDatabaseFirstPointerExpectation;
  readonly secondPointer: CanvasDatabaseFirstPointerExpectation;
  readonly stopsEditableTarget: boolean;
}

const COMMON_CANVAS_DATABASE_INTERACTION_ZONES = [
  {
    zone: 'select-move',
    firstPointer: 'select-node',
    secondPointer: 'select-node',
    stopsEditableTarget: false,
  },
  {
    zone: 'horizontal-resize',
    firstPointer: 'resize-node',
    secondPointer: 'resize-node',
    stopsEditableTarget: false,
  },
  {
    zone: 'toolbar-action',
    firstPointer: 'activate-target',
    secondPointer: 'activate-target',
    stopsEditableTarget: false,
  },
  {
    zone: 'fullscreen-action',
    firstPointer: 'activate-target',
    secondPointer: 'activate-target',
    stopsEditableTarget: false,
  },
  {
    zone: 'internal-window',
    firstPointer: 'scroll-preview',
    secondPointer: 'scroll-preview',
    stopsEditableTarget: false,
  },
  {
    zone: 'import-drop',
    firstPointer: 'run-import-preflight',
    secondPointer: 'run-import-preflight',
    stopsEditableTarget: false,
  },
] as const satisfies readonly CanvasDatabaseInteractionZoneContract[];

function createCanvasDatabaseInteractionZones(
  contentEditExpectation: CanvasDatabaseFirstPointerExpectation
) {
  return [
    ...COMMON_CANVAS_DATABASE_INTERACTION_ZONES,
    {
      zone: 'content-edit',
      firstPointer: contentEditExpectation,
      secondPointer: 'activate-target',
      stopsEditableTarget: false,
    },
  ] as const satisfies readonly CanvasDatabaseInteractionZoneContract[];
}

export const CANVAS_DATABASE_INTERACTION_MATRIX = {
  summary: createCanvasDatabaseInteractionZones('select-node'),
  table: createCanvasDatabaseInteractionZones('select-node'),
  kanban: createCanvasDatabaseInteractionZones('select-node'),
  gantt: createCanvasDatabaseInteractionZones('select-node'),
} as const satisfies Record<
  CanvasDatabasePresentationViewMode,
  readonly CanvasDatabaseInteractionZoneContract[]
>;

export function getCanvasDatabaseInteractionZones(
  mode: CanvasDatabasePresentationViewMode
): readonly CanvasDatabaseInteractionZoneContract[] {
  return CANVAS_DATABASE_INTERACTION_MATRIX[mode];
}

export function resolveCanvasDatabasePresentationInteractivity(input: {
  editing: boolean;
  selected: boolean;
  selectedCount: number;
}): boolean {
  return input.selectedCount === 1 && (input.editing || input.selected);
}

export function shouldRouteCanvasDatabasePointerToCanvasTool(
  toolName: string | undefined
): boolean {
  return (
    toolName !== undefined && toolName !== 'default' && toolName !== 'empty'
  );
}

export function shouldActivateCanvasDatabaseSelectionFromPointer(input: {
  button: number;
  editing: boolean;
  hasRangeModifier: boolean;
  isLocked: boolean;
  isPrimary: boolean;
  selected: boolean;
}): boolean {
  if (input.button !== 0 || !input.isPrimary) {
    return false;
  }
  if (input.hasRangeModifier || input.isLocked) {
    return false;
  }
  return !input.selected && !input.editing;
}

export function shouldKeepCanvasDatabaseActionSelectionNonEditing(input: {
  button: number;
  hasRangeModifier: boolean;
  isAction: boolean;
  isLocked: boolean;
  isPrimary: boolean;
}): boolean {
  return (
    input.isAction &&
    input.button === 0 &&
    input.isPrimary &&
    !input.hasRangeModifier &&
    !input.isLocked
  );
}

export function shouldFitCanvasDatabaseFromHeaderClick(input: {
  button: number;
  hasDragged: boolean;
  hasModifier: boolean;
  isInteractiveTarget: boolean;
}): boolean {
  return (
    input.button === 0 &&
    !input.hasDragged &&
    !input.hasModifier &&
    !input.isInteractiveTarget
  );
}
